import React, {FC, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {formatApiError} from '../../../shared/utils/apiErrorMessage'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'
import {useCertificateDeleteToast} from '../core/useCertificateDeleteToast'
import type {CertificateCategory, CompanyGroupAdminLite} from '../core/_models'
import {
  listCompanyGroupAdmins,
  listCertificateCategoryLibrary,
  createCertificateCategory,
  updateCertificateCategory,
  deleteCertificateCategory,
} from '../core/_requests'
import { QHSE_PERMS } from '../core/permissions'
import { isBroadFleetPickerUser } from '../core/qhseUserContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1060,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const emptyForm = {
  id: undefined as number | undefined,
  name: '',
  isActive: true,
  scopeType: 'COMPANY' as 'GLOBAL' | 'COMPANY',
  companyGroupId: '',
}

const CategoryLibraryModal: FC<{isOpen: boolean; onClose: () => void; targetType?: 'VESSEL' | 'OFFICE'}> = ({isOpen, onClose, targetType = 'VESSEL'}) => {
  const {auth, currentUser, can, isSuperAdmin} = useAuth()
  const confirmDelete = useCertificateDeleteToast(isOpen)
  const fleetPicker = isBroadFleetPickerUser(currentUser) || isSuperAdmin

  const canCreateCat = isSuperAdmin || can(QHSE_PERMS.CERT_CATEGORY_CREATE)
  const canUpdateCat = isSuperAdmin || can(QHSE_PERMS.CERT_CATEGORY_UPDATE)
  const canDeleteCat = isSuperAdmin || can(QHSE_PERMS.CERT_CATEGORY_DELETE)

  const canManageGlobalRows = isSuperAdmin || (fleetPicker && canUpdateCat)

  const canEditCategoryRow = (isGlobal: boolean) =>
    isGlobal ? canManageGlobalRows : canUpdateCat
  const canDeleteCategoryRow = (isGlobal: boolean) =>
    isGlobal ? fleetPicker && canDeleteCat : canDeleteCat

  const [rows, setRows] = useState<CertificateCategory[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(10)
  const [loading, setLoading] = useState(false)

  const [search, setSearch] = useState('')
  const [scopeFilter, setScopeFilter] = useState<'ALL' | 'GLOBAL' | 'COMPANY'>('ALL')
  const [companyGroupIdFilter, setCompanyGroupIdFilter] = useState('')

  const [companies, setCompanies] = useState<CompanyGroupAdminLite[]>([])

  const [form, setForm] = useState({...emptyForm})
  const isEditing = form.id != null
  const [showFormModal, setShowFormModal] = useState(false)

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / size)), [total, size])

  useEffect(() => {
    if (!isOpen) return
    if (fleetPicker) fetchCompanies()
  }, [isOpen, fleetPicker])

  useEffect(() => {
    if (!isOpen) return
    fetchLibrary()
  }, [isOpen, page, size, search, scopeFilter, companyGroupIdFilter])

  const fetchCompanies = async () => {
    if (!auth?.auth?.jwt) return
    try {
      const data = await listCompanyGroupAdmins(auth.auth.jwt)
      setCompanies(data)
    } catch (e: any) {
      toast.error('Failed to load companies')
    }
  }

  const fetchLibrary = async () => {
    if (!auth?.auth?.jwt) return
    if (fleetPicker && scopeFilter === 'COMPANY' && !companyGroupIdFilter) {
      setRows([])
      setTotal(0)
      return
    }
    setLoading(true)
    try {
      const {content, totalElements} = await listCertificateCategoryLibrary(auth.auth.jwt, {
        search: search.trim() || undefined,
        page,
        size,
        activeOnly: true,
        scope: fleetPicker ? scopeFilter : 'ALL',
        companyGroupId:
          fleetPicker && (scopeFilter === 'COMPANY' || (scopeFilter === 'ALL' && companyGroupIdFilter))
            ? companyGroupIdFilter
            : undefined,
        targetType,
      })
      setRows(content)
      setTotal(totalElements)
    } catch (e: any) {
      toast.error('Failed to load categories')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => setForm({...emptyForm})

  const handleEdit = (row: CertificateCategory) => {
    setForm({
      id: row.id,
      name: row.name || '',
      isActive: row.isActive ?? true,
      scopeType: (row.scopeType === 'GLOBAL' ? 'GLOBAL' : 'COMPANY'),
      companyGroupId: row.companyGroupAdminId ? String(row.companyGroupAdminId) : '',
    })
    setShowFormModal(true)
  }

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error('Category name is required')
      return
    }
    if (fleetPicker && form.scopeType === 'COMPANY' && !form.companyGroupId) {
      toast.error('Company is required for company scope')
      return
    }
    if (!auth?.auth?.jwt) {
      toast.error('Authentication token missing')
      return
    }

    const payload: any = {
      name: form.name.trim(),
      isActive: form.isActive,
      targetType,
    }

    if (fleetPicker) {
      payload.scopeType = form.scopeType
      if (form.scopeType === 'COMPANY') {
        payload.companyGroupAdminId = Number(form.companyGroupId)
      }
    }

    try {
      if (isEditing && form.id != null) {
        await updateCertificateCategory(auth.auth.jwt, form.id, payload)
      } else {
        await createCertificateCategory(auth.auth.jwt, payload)
      }
      toast.success(isEditing ? 'Category updated' : 'Category created')
      resetForm()
      setShowFormModal(false)
      fetchLibrary()
    } catch (e: any) {
      toast.error(formatApiError(e, 'Save failed', 'save'))
    }
  }

  const handleDelete = (row: CertificateCategory) => {
    const token = auth?.auth?.jwt
    if (!token) {
      toast.error('Authentication token missing')
      return
    }
    confirmDelete(`certificate-category-${targetType}-${row.id}`, `Delete category "${row.name}"?`, async () => {
      try {
        await deleteCertificateCategory(token, row.id)
        toast.success('Category deleted')
        fetchLibrary()
      } catch (e: any) {
        toast.error(formatApiError(e, 'Delete failed', 'delete'))
      }
    })
  }

  const companyNameById = useMemo(() => {
    const map = new Map<number, string>()
    companies.forEach((c) => map.set(c.id, c.name))
    return map
  }, [companies])

  if (!isOpen) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{width: '85vw', height: '80vh', maxWidth: '1200px', maxHeight: '800px'}}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <h5 className='modal-title text-dark m-0'>
            <KTSVG path='/media/icons/duotune/general/gen048.svg' className='svg-icon-3 me-2' />
            Category Library
          </h5>
          <button type='button' className='btn-close' onClick={onClose} />
        </div>

        <div className='flex-grow-1 overflow-auto px-4 py-3'>
          <div className='row g-3 align-items-end mb-5'>
            <div className='col-md-4'>
              <label className='form-label fw-semibold fs-6 mb-2 text-gray-700'>Search</label>
              <input
                type='text'
                className='form-control form-control-solid'
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(0)
                }}
                placeholder='Search category name...'
              />
            </div>

            {fleetPicker && (
              <>
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-6 mb-2 text-gray-700'>Scope</label>
                  <SearchableSelect
                    className='form-select form-select-solid'
                    value={scopeFilter}
                    onChange={(e) => {
                      setScopeFilter(e.target.value as any)
                      setPage(0)
                    }}
                  >
                    <option value='ALL'>All</option>
                    <option value='GLOBAL'>Global</option>
                    <option value='COMPANY'>Company</option>
                  </SearchableSelect>
                </div>
                <div className='col-md-3'>
                  <label className='form-label fw-semibold fs-6 mb-2 text-gray-700'>Company</label>
                  <SearchableSelect
                    className='form-select form-select-solid'
                    value={companyGroupIdFilter}
                    onChange={(e) => {
                      setCompanyGroupIdFilter(e.target.value)
                      setPage(0)
                    }}
                    disabled={scopeFilter === 'GLOBAL'}
                  >
                    <option value=''>Select company</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              </>
            )}

            {canCreateCat && (
              <div className='col-md-auto ms-auto'>
                <button
                  className='btn btn-primary'
                  type='button'
                  onClick={() => {
                    resetForm()
                    setShowFormModal(true)
                  }}
                >
                  Add Category
                </button>
              </div>
            )}
          </div>

          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th style={{width: '70px'}} className='text-center'>SR/NO</th>
                  <th>Category Name</th>
                  <th>Scope</th>
                  {fleetPicker && <th>Company</th>}
                  <th className='text-center'>Active</th>
                  <th className='text-end'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={fleetPicker ? 6 : 5} className='text-center py-6'>
                      Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={fleetPicker ? 6 : 5} className='text-center py-6'>
                      No categories found
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => {
                    const sr = page * size + idx + 1
                    const isGlobal = (row.scopeType || 'GLOBAL') === 'GLOBAL' || row.companyGroupAdminId == null
                    const canEditRow = canEditCategoryRow(isGlobal)
                    const canDelRow = canDeleteCategoryRow(isGlobal)
                    return (
                      <tr key={row.id}>
                        <td className='text-center'>{sr}</td>
                        <td>{row.name}</td>
                        <td>{isGlobal ? 'Global' : 'Company'}</td>
                        {fleetPicker && (
                          <td>
                            {isGlobal
                              ? '-'
                              : companyNameById.get(Number(row.companyGroupAdminId)) || `#${row.companyGroupAdminId}`}
                          </td>
                        )}
                        <td className='text-center'>
                          <span className={`badge ${row.isActive ? 'bg-light-success' : 'bg-light-danger'}`}>
                            {row.isActive ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className='text-end'>
                          <button
                            className='btn btn-icon btn-sm me-2'
                            title='Edit'
                            onClick={() => handleEdit(row)}
                            disabled={!canEditRow}
                          >
                            <KTSVG path='/media/map/edit-active.svg' />
                          </button>
                          <button
                            className='btn btn-icon btn-sm'
                            title='Delete'
                            onClick={() => handleDelete(row)}
                            disabled={!canDelRow}
                          >
                            <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3 text-danger' />
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className='d-flex justify-content-between align-items-center pt-3'>
            <div className='d-flex align-items-center'>
              <span className='text-muted me-2'>Rows per page</span>
              <SearchableSelect
                className='form-select form-select-solid form-select-sm'
                style={{width: '80px'}}
                value={size}
                onChange={(e) => {
                  setSize(Number(e.target.value))
                  setPage(0)
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </SearchableSelect>
            </div>
            <div className='d-flex align-items-center gap-3'>
              <span className='text-muted'>Page {page + 1} of {totalPages}</span>
              <div className='btn-group'>
                <button
                  className='btn btn-light btn-sm'
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Prev
                </button>
                <button
                  className='btn btn-light btn-sm'
                  onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
                  disabled={page + 1 >= totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className='border-top px-4 py-3 d-flex justify-content-end'>
          <button className='btn btn-light' onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {showFormModal && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', inset: 0, zIndex: 1070}}
        >
          <div className='modal-dialog modal-dialog-centered' style={{maxWidth: 520}}>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title text-dark'>
                  {isEditing ? 'Edit Category' : 'Add Category'}
                </h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => {
                    resetForm()
                    setShowFormModal(false)
                  }}
                />
              </div>

              <div className='modal-body'>
                <div className='mb-4'>
                  <label className='form-label required fw-semibold fs-6 mb-2 text-gray-700'>Name</label>
                  <input
                    type='text'
                    className='form-control form-control-solid'
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({...prev, name: e.target.value}))}
                  />
                </div>

                {fleetPicker && (
                  <div className='mb-4'>
                    <label className='form-label fw-semibold fs-6 mb-2 text-gray-700'>Scope</label>
                    <SearchableSelect
                      className='form-select form-select-solid'
                      value={form.scopeType}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          scopeType: e.target.value as 'GLOBAL' | 'COMPANY',
                          companyGroupId: e.target.value === 'GLOBAL' ? '' : prev.companyGroupId,
                        }))
                      }
                    >
                      <option value='GLOBAL'>Global</option>
                      <option value='COMPANY'>Company</option>
                    </SearchableSelect>
                  </div>
                )}

                {fleetPicker && form.scopeType === 'COMPANY' && (
                  <div className='mb-4'>
                    <label className='form-label required fw-semibold fs-6 mb-2 text-gray-700'>Company</label>
                    <SearchableSelect
                      className='form-select form-select-solid'
                      value={form.companyGroupId}
                      onChange={(e) => setForm((prev) => ({...prev, companyGroupId: e.target.value}))}
                    >
                      <option value=''>Select company</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                )}

                <div className='form-check form-switch'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={form.isActive}
                    onChange={(e) => setForm((prev) => ({...prev, isActive: e.target.checked}))}
                  />
                  <label className='form-check-label fw-semibold text-gray-700'>Active</label>
                </div>
              </div>

              <div className='modal-footer'>
                <button
                  className='btn btn-light'
                  type='button'
                  onClick={() => {
                    resetForm()
                    setShowFormModal(false)
                  }}
                >
                  Cancel
                </button>
                <button className='btn btn-primary' type='button' onClick={handleSubmit}>
                  {isEditing ? 'Save Changes' : 'Add Category'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export {CategoryLibraryModal}
